import { useState } from 'react'
import { Head, Link, router, usePage } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import DeleteConfirmModal from '@/components/DeleteConfirmModal'
import { ArrowLeft, Save, Trash2, Building2, PieChart, Plus, X, AlertCircle } from 'lucide-react'

interface SplitTier {
  commission_number: number
  agent_percentage: number
  sponsor_percentage: number
}

interface SplitSchedule {
  tiers: SplitTier[]
  default_agent_percentage: number
  default_sponsor_percentage: number
}

interface Carrier {
  id: number
  name: string
  code: string
  is_active: boolean
  carrier_payout_percentage: number
  commission_split_schedule: SplitSchedule
  commission_count: number
  created_at: string
}

interface Props extends PageProps {
  carrier: Carrier
}

export default function CarrierShow({ carrier }: Props) {
  const { auth } = usePage<PageProps>().props
  const canDelete = auth.user?.role === 'owner' || auth.user?.role === 'administrator'

  const [activeTab, setActiveTab] = useState<'details' | 'splits'>('details')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isEditingDetails] = useState(false)

  const schedule = carrier.commission_split_schedule || {
    tiers: [],
    default_agent_percentage: 90,
    default_sponsor_percentage: 10
  }

  const [tiers, setTiers] = useState<SplitTier[]>(
    (schedule.tiers || []).map((t: any) => ({
      commission_number: Number(t.commission_number),
      agent_percentage: Number(t.agent_percentage),
      sponsor_percentage: Number(t.sponsor_percentage)
    }))
  )
  const [defaultAgent, setDefaultAgent] = useState(Number(schedule.default_agent_percentage) || 90)
  const [defaultSponsor, setDefaultSponsor] = useState(Number(schedule.default_sponsor_percentage) || 10)
  const [splitError, setSplitError] = useState('')

  const addTier = () => {
    const maxNum = tiers.length > 0 ? Math.max(...tiers.map(t => t.commission_number)) : 0
    setTiers([...tiers, { commission_number: maxNum + 1, agent_percentage: 50, sponsor_percentage: 50 }])
  }

  const removeTier = (index: number) => {
    setTiers(tiers.filter((_, i) => i !== index))
  }

  const updateTier = (index: number, field: keyof SplitTier, value: number) => {
    const updated = [...tiers]
    updated[index] = { ...updated[index], [field]: value }
    if (field === 'agent_percentage') {
      updated[index].sponsor_percentage = 100 - value
    } else if (field === 'sponsor_percentage') {
      updated[index].agent_percentage = 100 - value
    }
    setTiers(updated)
  }

  const handleDefaultAgentChange = (val: number) => {
    setDefaultAgent(val)
    setDefaultSponsor(100 - val)
  }

  const saveSplitSchedule = () => {
    setSplitError('')

    for (const tier of tiers) {
      if (Math.abs(tier.agent_percentage + tier.sponsor_percentage - 100) > 0.01) {
        setSplitError(`Commission #${tier.commission_number}: percentages must total 100%.`)
        return
      }
      if (tier.agent_percentage < 0 || tier.sponsor_percentage < 0) {
        setSplitError(`Commission #${tier.commission_number}: percentages cannot be negative.`)
        return
      }
    }

    if (Math.abs(defaultAgent + defaultSponsor - 100) > 0.01) {
      setSplitError('Default percentages must total 100%.')
      return
    }

    router.patch(`/carriers/${carrier.id}/update_split_schedule`, {
      schedule: {
        tiers: tiers.sort((a, b) => a.commission_number - b.commission_number).map(t => ({
          commission_number: String(t.commission_number),
          agent_percentage: String(t.agent_percentage),
          sponsor_percentage: String(t.sponsor_percentage)
        })),
        default_agent_percentage: String(defaultAgent),
        default_sponsor_percentage: String(defaultSponsor)
      }
    } as any)
  }

  return (
    <AppLayout>
      <Head title={carrier.name} />

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link href="/carriers" className="p-2 text-chestnut-charcoal/60 hover:text-chestnut-forest">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">{carrier.name}</h1>
              <p className="text-chestnut-charcoal/70 mt-1">Code: {carrier.code}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {activeTab === 'details' && !isEditingDetails && (
              <>
                <Link href={`/carriers/${carrier.id}/edit`} className="btn-secondary">Edit</Link>
                {canDelete && carrier.commission_count === 0 && (
                  <button onClick={() => setShowDeleteModal(true)} className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg">
                    <Trash2 className="h-5 w-5" />
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-chestnut-gray">
          <div className="border-b border-chestnut-gray">
            <div className="flex">
              <button
                onClick={() => setActiveTab('details')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'details'
                    ? 'border-chestnut-forest text-chestnut-forest'
                    : 'border-transparent text-chestnut-charcoal/60 hover:text-chestnut-charcoal'
                }`}
              >
                <span className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Details
                </span>
              </button>
              <button
                onClick={() => setActiveTab('splits')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'splits'
                    ? 'border-chestnut-forest text-chestnut-forest'
                    : 'border-transparent text-chestnut-charcoal/60 hover:text-chestnut-charcoal'
                }`}
              >
                <span className="flex items-center gap-2">
                  <PieChart className="h-4 w-4" />
                  Commission Splits
                </span>
              </button>
            </div>
          </div>

          <div className="p-6">
            {activeTab === 'details' ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">Carrier Name</label>
                    <p className="text-chestnut-charcoal font-medium">{carrier.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">Code</label>
                    <p className="text-chestnut-charcoal font-mono">{carrier.code}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">Status</label>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${carrier.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {carrier.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">Total Commissions</label>
                    <p className="text-chestnut-charcoal">{carrier.commission_count}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">Carrier Payout Rate</label>
                    <p className="text-chestnut-charcoal font-medium">{Number(carrier.carrier_payout_percentage).toFixed(2)}%</p>
                    <p className="text-xs text-chestnut-charcoal/60 mt-0.5">
                      Portion of gross commission the carrier actually pays Chestnut.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-chestnut-charcoal mb-1">Graduated Split Schedule</h3>
                  <p className="text-sm text-chestnut-charcoal/60">
                    Configure how commissions are split between the writing agent and their sponsoring agent.
                    Changes only apply to new commissions created after saving.
                  </p>
                </div>

                {splitError && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    {splitError}
                  </div>
                )}

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-chestnut-charcoal">Commission Tiers</h4>
                    <button onClick={addTier} className="btn-secondary text-sm flex items-center gap-1">
                      <Plus className="h-4 w-4" />
                      Add Tier
                    </button>
                  </div>

                  {tiers.length === 0 ? (
                    <p className="text-sm text-chestnut-charcoal/60 italic">No graduated tiers configured. All commissions will use the default split below.</p>
                  ) : (
                    <div className="border border-chestnut-charcoal/10 rounded-lg overflow-hidden">
                      <table className="min-w-full">
                        <thead>
                          <tr className="bg-chestnut-cream/50">
                            <th className="px-4 py-2.5 text-left text-sm font-semibold text-chestnut-charcoal">Commission #</th>
                            <th className="px-4 py-2.5 text-left text-sm font-semibold text-chestnut-charcoal">Writing Agent %</th>
                            <th className="px-4 py-2.5 text-left text-sm font-semibold text-chestnut-charcoal">Sponsor %</th>
                            <th className="px-4 py-2.5 text-right text-sm font-semibold text-chestnut-charcoal">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-chestnut-charcoal/10">
                          {tiers.map((tier, index) => (
                            <tr key={index}>
                              <td className="px-4 py-2.5">
                                <input
                                  type="number"
                                  value={tier.commission_number}
                                  onChange={(e) => updateTier(index, 'commission_number', parseInt(e.target.value) || 1)}
                                  className="w-20 px-2 py-1 border border-chestnut-charcoal/20 rounded text-sm"
                                  min="1"
                                />
                              </td>
                              <td className="px-4 py-2.5">
                                <div className="flex items-center gap-1">
                                  <input
                                    type="number"
                                    value={tier.agent_percentage}
                                    onChange={(e) => updateTier(index, 'agent_percentage', parseFloat(e.target.value) || 0)}
                                    className="w-20 px-2 py-1 border border-chestnut-charcoal/20 rounded text-sm"
                                    min="0"
                                    max="100"
                                    step="0.01"
                                  />
                                  <span className="text-sm text-chestnut-charcoal/60">%</span>
                                </div>
                              </td>
                              <td className="px-4 py-2.5">
                                <div className="flex items-center gap-1">
                                  <input
                                    type="number"
                                    value={tier.sponsor_percentage}
                                    onChange={(e) => updateTier(index, 'sponsor_percentage', parseFloat(e.target.value) || 0)}
                                    className="w-20 px-2 py-1 border border-chestnut-charcoal/20 rounded text-sm"
                                    min="0"
                                    max="100"
                                    step="0.01"
                                    readOnly
                                  />
                                  <span className="text-sm text-chestnut-charcoal/60">%</span>
                                </div>
                              </td>
                              <td className="px-4 py-2.5 text-right">
                                <button
                                  onClick={() => removeTier(index)}
                                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t border-chestnut-charcoal/10">
                  <h4 className="font-medium text-chestnut-charcoal mb-3">Default Split (after all tiers)</h4>
                  <p className="text-sm text-chestnut-charcoal/60 mb-3">
                    Applied to all commissions beyond the numbered tiers above.
                  </p>
                  <div className="grid grid-cols-2 gap-4 max-w-md">
                    <div>
                      <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">Writing Agent %</label>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          value={defaultAgent}
                          onChange={(e) => handleDefaultAgentChange(parseFloat(e.target.value) || 0)}
                          className="w-24 px-3 py-2 border border-chestnut-charcoal/20 rounded-lg"
                          min="0"
                          max="100"
                          step="0.01"
                        />
                        <span className="text-sm text-chestnut-charcoal/60">%</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">Sponsor %</label>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          value={defaultSponsor}
                          className="w-24 px-3 py-2 border border-chestnut-charcoal/20 rounded-lg bg-gray-50"
                          readOnly
                        />
                        <span className="text-sm text-chestnut-charcoal/60">%</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-chestnut-charcoal/10">
                  <div className="bg-chestnut-cream/50 rounded-lg p-4 mb-4">
                    <h4 className="font-medium text-chestnut-charcoal text-sm mb-2">Preview</h4>
                    <div className="space-y-1 text-sm text-chestnut-charcoal/80">
                      {tiers
                        .sort((a, b) => a.commission_number - b.commission_number)
                        .map(tier => (
                          <div key={tier.commission_number}>
                            Commission #{tier.commission_number}: <strong>{tier.agent_percentage}%</strong> to agent, <strong>{tier.sponsor_percentage}%</strong> to sponsor
                          </div>
                        ))}
                      <div>
                        Commission #{(tiers.length > 0 ? Math.max(...tiers.map(t => t.commission_number)) : 0) + 1}+: <strong>{defaultAgent}%</strong> to agent, <strong>{defaultSponsor}%</strong> to sponsor
                      </div>
                    </div>
                  </div>

                  <button onClick={saveSplitSchedule} className="btn-primary flex items-center gap-2">
                    <Save className="h-4 w-4" />
                    Save Split Schedule
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Carrier"
        message="Are you sure you want to delete this carrier? This action cannot be undone."
        deleteUrl={`/carriers/${carrier.id}`}
        itemName={carrier.name}
        redirectUrl="/carriers"
      />
    </AppLayout>
  )
}
